import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Alert, Avatar, Button, Card, Input, Label } from '@/components/ui';
import { Logo } from '@/components/Logo';
import { DynamicForm, type FormSchema } from '@/components/dynamic-form';
import { api, ApiError } from '@/lib/api';
import type { PublicSupportedTipo } from '@server-types/custom-fields-publico';

/**
 * FE2 — Formulário PÚBLICO de captação de lead (sem login).
 *
 * Rotas: `/f/:slug` (link pessoal do vendedor) e `/f/contato` (link geral).
 * Standalone (sem AppShell), mobile-first, identidade IGM com LOGO REAL.
 *
 * - Campos DINÂMICOS de GET /public/captacao/form-config (obrigatoriedade vem
 *   da config de campos do lead).
 * - Variante pessoal: mostra a faixa "Seu atendimento será com <Nome>".
 * - Honeypot invisível `website` (FE3) — anti-spam sem captcha.
 * - Pós-envio: confirmação sóbria.
 *
 * Sem armazenamento local no navegador. Chamadas com `skipAuth` (público).
 *
 * K-01 (2026-07-30): os campos personalizados (PF9) eram desenhados por um
 * renderizador INLINE aqui dentro, que só sabia 4 tipos e chaveava o estado
 * por um `name` próprio. Agora quem desenha é o `DynamicForm` — o mesmo
 * componente das telas internas. **Não volte a desenhar campo personalizado
 * aqui**: dois renderizadores evoluem separados e quem mexe num não sabe do
 * outro (foi o que aconteceu na FASE-C).
 */

interface CampoConfig {
  name: string;
  label: string;
  required: boolean;
}

/**
 * PF9 — campos personalizados ligados pro público, como vêm do
 * `GET /form-config`. `tipo` é o CANÔNICO (`text|number|dropdown|checkbox`),
 * o mesmo que o construtor grava — sem tradução no meio do caminho.
 */
interface CampoCustomConfig {
  customFieldId: string;
  label: string;
  required: boolean;
  tipo: PublicSupportedTipo;
  placeholder: string | null;
  opcoes: Array<{ value: string; label: string }>;
}

interface Vendedor {
  nome: string;
  iniciais: string;
}

export function FormCaptacaoPage() {
  const params = useParams<{ slug?: string }>();
  // `/f/contato` cai aqui com slug undefined (rota própria) → captação geral.
  const slug = params.slug ?? 'contato';
  const isGeral = slug === 'contato';
  // I6b: foto real do vendedor via endpoint público que expõe SÓ a imagem
  // (proxy server-side; 404 → o Avatar cai pras iniciais). Nada além da foto.
  const fotoVendedorUrl = isGeral
    ? null
    : `${import.meta.env.VITE_API_BASE_URL || ''}/api/v1/public/captacao/vendedor/${encodeURIComponent(slug)}/foto`;

  const [campos, setCampos] = useState<CampoConfig[]>([]);
  const [camposCustom, setCamposCustom] = useState<CampoCustomConfig[]>([]);
  const [vendedor, setVendedor] = useState<Vendedor | null>(null);
  const [valores, setValores] = useState<Record<string, string>>({});
  // K-01: chaveado por `customFieldId` — a MESMA chave que vai no POST. Antes
  // o estado usava um `name` próprio e remapeava no envio; dois nomes pro
  // mesmo campo é como se perde valor no meio do caminho.
  const [valoresCustom, setValoresCustom] = useState<Record<string, unknown>>({});
  const [website, setWebsite] = useState(''); // honeypot
  const [carregando, setCarregando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [enviado, setEnviado] = useState(false);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const cfg = await api.get<{ campos: CampoConfig[]; camposCustom?: CampoCustomConfig[] }>(
          '/api/v1/public/captacao/form-config',
          { skipAuth: true },
        );
        if (vivo) {
          setCampos(cfg.campos);
          setCamposCustom(cfg.camposCustom ?? []);
        }
      } catch {
        if (vivo) setErro('Não foi possível carregar o formulário. Tente recarregar a página.');
      }
      if (!isGeral) {
        try {
          const v = await api.get<{ vendedor: Vendedor | null }>(
            `/api/v1/public/captacao/vendedor/${encodeURIComponent(slug)}`,
            { skipAuth: true },
          );
          if (vivo) setVendedor(v.vendedor);
        } catch {
          if (vivo) setVendedor(null);
        }
      }
      if (vivo) setCarregando(false);
    })();
    return () => {
      vivo = false;
    };
  }, [slug, isGeral]);

  function setCampo(name: string, value: string) {
    setValores((prev) => ({ ...prev, [name]: value }));
  }

  /**
   * Schema no formato canônico pro `DynamicForm`. `useMemo` é obrigatório: o
   * componente usa o objeto como dependência do efeito que inicializa os
   * valores — schema novo a cada render zeraria o que a pessoa digitou.
   *
   * Só traduz FORMATO (config pública → `FormFieldSchema`); o `tipo` passa
   * inteiro, sem mapa de conversão.
   */
  const schemaCustom = useMemo<FormSchema | undefined>(() => {
    if (camposCustom.length === 0) return undefined;
    return {
      entidade: 'lead',
      entidadeId: null,
      scopeTipo: null,
      scopeId: null,
      generatedAtIso: '', // schema público não carrega carimbo de geração
      campos: camposCustom.map((c, i) => ({
        // `id` é a chave dos valores dentro do DynamicForm — e é o
        // customFieldId, exatamente o que o POST espera.
        id: c.customFieldId,
        key: c.customFieldId,
        label: c.label,
        descricao: null,
        tipo: c.tipo,
        opcoes: c.opcoes,
        // 500 = o mesmo teto que o servidor aplica em campo de texto público
        // (`validarCamposCustom`). Sem isso o excedente sumia calado.
        validacao: c.tipo === 'text' ? { maxLength: 500 } : null,
        computedExpression: null,
        defaultValue: null,
        placeholder: c.placeholder,
        prefix: null,
        suffix: null,
        largura: 'full' as const,
        ordem: i,
        grupo: null,
        icone: null,
        obrigatorio: c.required,
        obrigatoriedadeMensagem: null,
        editable: true,
        valor: null,
      })),
    };
  }, [camposCustom]);

  // `useCallback` com deps vazias: o DynamicForm avisa por efeito com a
  // callback na lista de dependências — função nova a cada render viraria
  // laço de render.
  const receberValoresCustom = useCallback((v: Record<string, unknown>) => {
    setValoresCustom(v);
  }, []);

  const faltaObrigatorio =
    campos.some((c) => c.required && !(valores[c.name] ?? '').trim()) ||
    camposCustom.some((c) => {
      if (!c.required) return false;
      const v = valoresCustom[c.customFieldId];
      // checkbox obrigatório = precisa estar marcado (ex.: consentimento).
      if (c.tipo === 'checkbox') return v !== true;
      // number chega como número (o DynamicForm converte no onChange).
      if (typeof v === 'number') return !Number.isFinite(v);
      return !(typeof v === 'string' ? v.trim() : v);
    });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      // PF9 — monta { [customFieldId]: valor } só com o que foi preenchido.
      // O contrato do POST não mudou no K-01: a chave continua sendo o
      // customFieldId (o que mudou é que o estado já nasce assim, sem remapa).
      const camposCustomPayload: Record<string, string | number | boolean> = {};
      for (const c of camposCustom) {
        const v = valoresCustom[c.customFieldId];
        if (c.tipo === 'checkbox') {
          // Só sobe quando marcado: desmarcado = campo não respondido.
          if (v === true) camposCustomPayload[c.customFieldId] = true;
          continue;
        }
        if (typeof v === 'number' && Number.isFinite(v)) {
          camposCustomPayload[c.customFieldId] = v;
          continue;
        }
        if (typeof v === 'string' && v.trim() !== '') {
          camposCustomPayload[c.customFieldId] = v.trim();
        }
      }

      await api.post(
        '/api/v1/public/captacao/lead',
        {
          slug: isGeral ? null : slug,
          telefone: valores.telefone ?? '',
          nome: valores.nome ?? '',
          empresa: valores.empresa ?? null,
          necessidade: valores.necessidade ?? null,
          camposCustom: camposCustomPayload,
          website, // honeypot
        },
        { skipAuth: true },
      );
      setEnviado(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setErro('Recebemos muitas tentativas. Aguarde alguns instantes e tente de novo.');
      } else if (err instanceof ApiError && err.status === 400) {
        setErro('Confira os campos: telefone e nome são obrigatórios.');
      } else {
        setErro('Não foi possível enviar agora. Tente novamente em instantes.');
      }
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-paper-warm dark:bg-paper-dark-warm p-4">
      <div className="max-w-md w-full">
        {/* Cabeçalho com LOGO REAL + filete vermelho.
            PF10: logo em destaque (1ª coisa que o visitante vê) — ~44px no
            mobile, ~64px no desktop. Asset real (/logo.png ≥400px), sem
            esticar bitmap; className sobrepõe o h-8 do size="md" via twMerge. */}
        <div className="flex justify-center pt-2 mb-5">
          <Logo size="md" className="h-11 md:h-16" />
        </div>
        <div className="h-1 w-16 bg-red rounded-full mx-auto mb-6" aria-hidden="true" />

        <Card className="w-full">
          {enviado ? (
            <div className="p-8 text-center">
              {/* Ícone SVG no lugar do glifo "✓" solto (regra no-emoji-icons). */}
              <div
                className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-success-tint text-success dark:bg-success/20"
                aria-hidden="true"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m5 12.5 4.5 4.5L19 7" />
                </svg>
              </div>
              <h1 className="mb-2 font-display text-3xl tracking-tight text-ink-1 dark:text-ink-dark-1">
                Recebemos seus dados
              </h1>
              <p className="text-sm text-ink-2 dark:text-ink-dark-2">
                Nossa equipe entra em contato em breve.
              </p>
            </div>
          ) : (
            <div className="p-6">
              <h1 className="mb-1 font-display text-3xl tracking-tight text-ink-1 dark:text-ink-dark-1">
                Fale com a IGM
              </h1>
              <p className="mb-4 text-sm text-ink-2 dark:text-ink-dark-2">
                Deixe seus dados e nossa equipe retorna para entender o que você precisa.
              </p>

              {/* Variante pessoal: faixa do vendedor */}
              {!isGeral && vendedor && (
                <div className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-paper-surface dark:bg-paper-dark-surface border border-line dark:border-line-dark">
                  <Avatar name={vendedor.nome} src={fotoVendedorUrl} size="md" />
                  <p className="text-sm text-ink-2 dark:text-ink-dark-2">
                    Seu atendimento será com{' '}
                    <span className="font-medium text-ink-1 dark:text-ink-dark-1">
                      {vendedor.nome}
                    </span>
                    , consultor comercial.
                  </p>
                </div>
              )}

              {erro && (
                <Alert variant="danger" rail className="mb-3">
                  {erro}
                </Alert>
              )}

              {carregando ? (
                <p className="py-6 text-center text-sm text-ink-2 dark:text-ink-dark-2" aria-busy="true">
                  Carregando…
                </p>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-3">
                  {campos.map((campo) =>
                    campo.name === 'necessidade' ? (
                      <div key={campo.name}>
                        <Label htmlFor={`cap-${campo.name}`} required={campo.required}>
                          {campo.label}
                        </Label>
                        <textarea
                          id={`cap-${campo.name}`}
                          value={valores[campo.name] ?? ''}
                          onChange={(e) => setCampo(campo.name, e.target.value)}
                          rows={4}
                          required={campo.required}
                          maxLength={2000}
                          className="w-full rounded-xl bg-paper-surface dark:bg-paper-dark-surface text-ink-1 dark:text-ink-dark-1 placeholder-ink-3 border border-line dark:border-line-dark px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:border-red focus:ring-red-soft"
                          data-testid={`cap-${campo.name}`}
                        />
                      </div>
                    ) : (
                      <div key={campo.name}>
                        <Label htmlFor={`cap-${campo.name}`} required={campo.required}>
                          {campo.label}
                        </Label>
                        <Input
                          id={`cap-${campo.name}`}
                          type={campo.name === 'telefone' ? 'tel' : 'text'}
                          value={valores[campo.name] ?? ''}
                          onChange={(e) => setCampo(campo.name, e.target.value)}
                          required={campo.required}
                          maxLength={campo.name === 'empresa' ? 160 : campo.name === 'telefone' ? 30 : 120}
                          placeholder={campo.name === 'telefone' ? '(11) 90000-0000' : undefined}
                          data-testid={`cap-${campo.name}`}
                        />
                      </div>
                    ),
                  )}

                  {/* PF9 — campos personalizados, desenhados pelo renderizador
                      CANÔNICO. `testIdPrefix="cap-cf"` mantém o contrato
                      `cap-cf-<customFieldId>` que existe desde o PF9; `noSubmit`
                      porque o envio é o desta página (POST público, sem auth),
                      não o `/api/v1/forms/submit`, que é autenticado. */}
                  {schemaCustom && (
                    <DynamicForm
                      entidade="lead"
                      schemaExterno={schemaCustom}
                      testIdPrefix="cap-cf"
                      noSubmit
                      onValuesChange={receberValoresCustom}
                    />
                  )}

                  {/* Honeypot invisível (FE3) — humano nunca vê nem preenche. */}
                  <div
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      left: '-9999px',
                      width: '1px',
                      height: '1px',
                      overflow: 'hidden',
                    }}
                  >
                    <label htmlFor="cap-website">Não preencha este campo</label>
                    <input
                      id="cap-website"
                      type="text"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                    />
                  </div>

                  <Button
                    type="submit"
                    loading={enviando}
                    disabled={faltaObrigatorio}
                    className="w-full"
                    data-testid="cap-submit"
                  >
                    Enviar
                  </Button>
                </form>
              )}

              {/* Nota de privacidade */}
              <p className="mt-4 text-xs leading-relaxed text-ink-2 dark:text-ink-dark-2">
                Seus dados são usados apenas para que nossa equipe entre em contato sobre esta
                solicitação. Não compartilhamos suas informações com terceiros.
              </p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
